import { zodResolver } from '@hookform/resolvers/zod';
import { useWebgate } from '@xvia/webgate-connect';
import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useCheckSignatureStatus } from '@hooks/document-signer/useCheckSignatureStatus';
import { useDownloadDocument } from '@hooks/document-signer/useDownloadDocument';
import { useRequestSignature } from '@hooks/document-signer/useRequestSignature';
import { useUploadFile } from '@hooks/document-signer/useUploadFile';

import { DocumentSigner } from './DocumentSigner';

export interface FormValues {
  file: FileList;
}

export interface FeedbackProps {
  type: 'success' | 'error';
  message: string;
}

export const schema = z.object({
  file: z
    .custom<FileList>()
    .refine((value: FileList) => !!value && value.length > 0, 'O arquivo é obrigatório')
    .refine(
      (value: FileList) => !!value && value[0]?.type === 'application/pdf',
      'Somente arquivos PDF são permitidos',
    ),
});

export const DocumentSignerContainer: React.FC = () => {
  const { accessToken, userInfo } = useWebgate();
  const [feedback, setFeedback] = React.useState<FeedbackProps | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [fileUrl, setFileUrl] = React.useState<string | null>(null);
  const [coordinates, setCoordinates] = React.useState<{ x: number; y: number } | null>(null);

  const token = accessToken ?? '';

  const uploadFile = useUploadFile(token);
  const requestSignature = useRequestSignature(token);
  const checkSignatureStatus = useCheckSignatureStatus(token);
  const downloadDocument = useDownloadDocument(token);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);

    if (file) {
      setFileUrl(URL.createObjectURL(file));
    }
  };

  const onSubmit: SubmitHandler<FormValues> = () => {
    if (!selectedFile || !coordinates) {
      setFeedback({ type: 'error', message: 'Envie um arquivo e selecione um ponto no PDF.' });
      return;
    }

    if (!userInfo?.preferred_username) {
      setFeedback({ type: 'error', message: 'Informações do usuário não encontradas.' });
      return;
    }

    uploadFile.mutate(selectedFile, {
      onSuccess: ({ fileId }) => {
        requestSignature.mutate(
          { fileId, userCpf: userInfo.preferred_username!, coordinates },
          {
            onSuccess: () => {
              setFeedback({ type: 'success', message: 'Documento enviado para assinatura com sucesso!' });
              reset();
              setSelectedFile(null);
            },
            onError: () => setFeedback({ type: 'error', message: 'Falha ao solicitar assinatura. Tente novamente.' }),
          },
        );
      },
      onError: () => setFeedback({ type: 'error', message: 'Falha ao fazer upload do arquivo. Tente novamente.' }),
    });
  };

  const onCheckStatus = () => {
    if (!requestSignature.data?.orderId) {
      setFeedback({ type: 'error', message: 'ID da ordem não encontrado.' });
      return;
    }

    checkSignatureStatus.mutate(requestSignature.data.orderId, {
      onSuccess: ({ status }) => setFeedback({ type: 'success', message: `Status da assinatura: ${status}` }),
      onError: () => setFeedback({ type: 'error', message: 'Erro ao verificar status da assinatura.' }),
    });
  };

  const onDownload = () => {
    /* v8 ignore next 3 */
    if (!uploadFile.data?.fileId) {
      setFeedback({ type: 'error', message: 'ID do arquivo não encontrado.' });
      return;
    }

    downloadDocument.mutate(uploadFile.data.fileId, {
      onError: () => setFeedback({ type: 'error', message: 'Erro ao baixar o documento.' }),
    });
  };

  return (
    <DocumentSigner
      onSubmit={handleSubmit(onSubmit)}
      register={register}
      errors={errors}
      feedback={feedback}
      onCloseFeedback={() => setFeedback(null)}
      isAuthenticated={!!accessToken}
      isSubmitting={uploadFile.isPending || requestSignature.isPending}
      isCheckingStatus={checkSignatureStatus.isPending}
      onFileChange={onFileChange}
      onDownload={onDownload}
      onCheckStatus={onCheckStatus}
      signatureStatus={checkSignatureStatus.data?.status}
      fileId={uploadFile.data?.fileId}
      onPdfClick={(x, y) => setCoordinates({ x, y })}
      coordinates={coordinates}
      fileUrl={fileUrl}
    />
  );
};
